<template>
  <div class="skeleton-graph-page">
    <Row :gutter="16" :style="{ height: height + 'px' }">
      <!-- 左侧地图 -->
      <Col span="16" :style="{ height: '100%' }">
        <Card :bordered="false" dis-hover :style="{ height: '100%', position: 'relative' }">
          <!-- 编辑模式工具栏 -->
          <div v-if="editMode" class="edit-toolbar">
            <span class="edit-mode-label">
              <Icon type="md-create" />
              {{ langMap.skeletonGraphEditMode[lang] }}
            </span>
          </div>

          <div class="skeleton-map-container" :style="{ height: '100%' }">
            <div id="skeleton_graph_map_id" class="map"></div>
          </div>
        </Card>
      </Col>

      <!-- 右侧列表面板 -->
      <Col span="8" :style="{ height: '100%'}">
        <Card :bordered="false" dis-hover :style="{ height: '100%',padding:'8px'  }">
          <!-- 编辑模式：显示编辑面板 -->
          <div v-if="editMode" :style="{ height: '100%' }">
            <EditPanel
              :graph-data="editingGraphData"
              :station-list="stationList"
              :is-map-add-mode="isMapAddNodeMode"
              :is-manual-connect-mode="isManualConnectMode"
              :is-creating-new="isCreatingNew"
              @update:graphData="handleGraphDataUpdate"
              @save="saveEdit"
              @cancel="cancelEdit"
              @map-add-node="handleMapAddNodeClick"
              @manual-connect="handleManualConnectClick"
              @import-data="showImportDialog"
              @delete-node="handleDeleteNodeFromPanel"
              @update-node="handleUpdateNodeFromPanel"
              @station-change="handleStationChange"
              @change-status="handleChangeStatus"
              @connect-mode-change="handleConnectModeChange"
            />
          </div>

          <!-- 非编辑模式：显示列表 -->
          <div v-else :style="{ height: '100%', display: 'flex', flexDirection: 'column' }">
            <div slot="title" style="margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 16px; font-weight: 600;">{{ langMap.skeletonGraphManage[lang] }}</span>
              <Button
                type="primary"
                size="small"
                @click="createNewGraph"
              >
                {{ langMap.newSkeletonGraph[lang] }}
              </Button>
            </div>

            <Table
              :columns="tableColumns"
              :data="graphList"
              :loading="tableLoading"
              :row-class-name="rowClassName"
              @on-row-click="handleRowClick"
              size="small"
              :height="height - 50"
              border
            >
              <template slot-scope="{ row }" slot="action">
                <Button 
                  type="primary" 
                  size="small" 
                  icon="md-create"
                  @click.stop="handleEditEdges(row.id)"
                  style="margin-right: 5px"
                  :title="langMap.edit[lang]"
                >
                </Button>
                <Button 
                  v-if="!editMode"
                  type="info" 
                  size="small" 
                  icon="md-git-network"
                  @click.stop="handleViewTopology(row.id)"
                  style="margin-right: 5px"
                  :title="langMap.skeletonGraphTopology[lang]"
                >
                </Button>
                <Button 
                  type="error" 
                  size="small" 
                  icon="md-trash" 
                  :title="langMap.delete[lang]"
                  @click.stop="handleDeleteClick(row.id)"
                >
                </Button>
              </template>
            </Table>
          </div>
        </Card>
      </Col>
    </Row>

    <!-- 新建骨架图对话框 -->
    <Modal
      v-model="graphDialogVisible"
      :title="langMap.newSkeletonGraph[lang]"
      width="800"
      :mask-closable="false"
    >
      <Form :model="graphForm" :rules="graphFormRules" ref="graphForm" :label-width="100">
        <FormItem :label="langMap.name[lang]" prop="name">
          <Input v-model="graphForm.name" :placeholder="langMap.inputName[lang]" />
        </FormItem>
        <FormItem :label="langMap.belongStation[lang]" prop="stationId">
          <Select v-model="graphForm.stationId" :placeholder="langMap.pleaseSelect[lang]">
            <Option
              v-for="station in stationList"
              :key="station.id"
              :value="station.id"
            >
              {{ station.name }}
            </Option>
          </Select>
        </FormItem>
        <FormItem :label="langMap.description[lang]">
          <Input 
            v-model="graphForm.description" 
            type="textarea" 
            :rows="3"
            :placeholder="langMap.inputDescription[lang]" 
          />
        </FormItem>
        <FormItem :label="langMap.importData[lang]">
          <div class="import-container">
            <div class="toolbar">
              <Button size="small" @click="formatJSON">{{ langMap.skeletonGraphFormatJson[lang] }}</Button>
              <Button size="small" @click="pasteFromClipboard">{{ langMap.skeletonGraphPaste[lang] }}</Button>
              <Button size="small" @click="clearJSON">{{ langMap.skeletonGraphClear[lang] }}</Button>
              <Upload
                :before-upload="beforeUpload"
                :show-upload-list="false"
                accept=".json"
                action=""
                style="display: inline-block; margin-left: auto;"
              >
                <Button size="small" icon="ios-cloud-upload">
                  {{ langMap.clickUpload[lang] }}
                </Button>
              </Upload>
            </div>
            
            <Input
              v-model="jsonInput"
              type="textarea"
              :rows="8"
              :placeholder="langMap.pasteJsonHere[lang]"
              @on-change="handleJsonInputChange"
            />

            <div v-if="importedNodes.length > 0" class="import-result">
              <Alert type="success" show-icon>
                {{ langMap.skeletonGraphValidationSuccess[lang] }}
              </Alert>
              <div class="result-summary">
                <span><strong>{{ langMap.skeletonGraphNodeCount[lang] }}:</strong> {{ importedNodes.length }}</span>
                <span style="margin-left: 20px"><strong>{{ langMap.skeletonGraphEdgeCount[lang] }}:</strong> {{ importedEdges.length }}</span>
              </div>
            </div>

            <div v-if="importErrors.length > 0" class="import-errors">
              <Alert type="error" show-icon>
                {{ langMap.skeletonGraphValidationFailed[lang] }}
              </Alert>
              <ul class="error-list">
                <li v-for="(error, index) in importErrors" :key="index">{{ error }}</li>
              </ul>
            </div>
          </div>
        </FormItem>
      </Form>
      
      <div slot="footer">
        <Button type="text" @click="cancelGraphDialog">
          {{ langMap.cancel[lang] }}
        </Button>
        <Button type="primary" :loading="btnLoading" @click="confirmGraphDialog">
          {{ langMap.save[lang] }}
        </Button>
      </div>
    </Modal>

    <!-- 拓扑图查看对话框 -->
    <topology-view-dialog
      v-model="topologyDialogVisible"
      :graph-data="currentTopologyData"
    />

    <!-- 数据导入对话框 -->
    <data-import-dialog
      v-model="importDialogVisible"
      @import-success="handleImportSuccess"
    />

    <!-- 页面加载动画 -->
    <Spin fix v-if="pageLoading">
      <Icon type="ios-loading" size="18" class="demo-spin-icon-load"></Icon>
      <div>{{ langMap.loading[lang] }}</div>
    </Spin>
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import langMap from '@/utils/language';
import api from '../../api';
import SkeletonGraphMap from './mapUnit/leaflet.vue';
import DataImportDialog from './components/DataImportDialog.vue';
import TopologyViewDialog from './components/TopologyViewDialog.vue';
import EditPanel from './components/EditPanel.vue';
import { validateTopology, isGraphConnected } from '@/utils/skeletonGraph/topologyValidator';
import coordinateConverter from '@/utils/skeletonGraph/coordinateConverter';
import autoConnector from '@/utils/skeletonGraph/autoConnector';
import { mapState } from 'vuex';

export default {
  name: 'SkeletonGraphIndex',
  components: {
    SkeletonGraphMap,
    DataImportDialog,
    TopologyViewDialog,
    EditPanel
  },
  data() {
    return {
      langMap,
      height: 600,
      mapLoading: true,
      graphList: [],
      currentGraphId: null,
      stationList: [],
      tableLoading: false,
      btnLoading: false,
      graphDialogVisible: false,
      importDialogVisible: false,
      topologyDialogVisible: false,
      jsonInput: '',
      importErrors: [],
      graphForm: {
        name: '',
        stationId: null,
        description: ''
      },
      importedNodes: [],
      importedEdges: [],
      currentTopologyData: { nodes: [], edges: [] },
      editMode: false,
      editingGraphId: null,
      editingGraphData: null,
      isCreatingNew: false, // 标记是否是新增操作
      parseTimer: null,
      tempGraphId: 1000000, // 临时ID起始值
      currentPopup: null, // 当前打开的弹窗
      currentEdgePopup: null, // 当前打开的连线弹窗
      currentEdgePopupInfo: null, // 当前连线弹窗的信息 { edge, graphId }
      selectedNodeForConnect: null, // 自动连线时选中的第一个节点
      isMapAddNodeMode: false, // 是否处于地图添加节点模式
      mapClickHandler: null, // 地图点击处理器引用
      isManualConnectMode: false, // 是否处于手动连线模式
      selectedNodeForConnect: null, // 手动连线时选中的第一个节点
      clickedNodeId: null, // 被点击查看的节点ID（用于显示不同颜色）
      hasUnsavedChanges: false, // 是否有未保存的修改
      lastAddedNodeId: null, // 最后添加的节点ID（用于顺序连接）
      stationChanged: false, // 标记站点是否发生变化（用于判断是否需要重新分配所有节点ID）
      // 地图相关
      map: null,
      graphLayers: {},
      nodeMarkers: {},
      edgeLines: {},
      edgeArrows: {}, // 存储箭头装饰器
      fenceLayers: [],
      fenceList: [],
      intersectionLayers: []
    };
  },
  computed: {
    lang() {
      return this.$store.state.app.language;
    },
    pageLoading() {
      return this.mapLoading || this.tableLoading;
    },
    canSaveGraph() {
      return this.graphForm.name && this.graphForm.stationId && this.importedNodes.length >= 2;
    },
    mapConfig() {
      return this.$store.state.app.leafletConfig;
    },
    areaColorList() {
      return this.$store.state.app.mapAreaColor;
    },
    graphFormRules() {
      return {
        name: [
          { required: true, message: this.langMap.skeletonGraphNameRequired[this.lang], trigger: 'blur' }
        ],
        stationId: [
          { required: true, type: 'number', message: this.langMap.skeletonGraphBelongStationRequired[this.lang], trigger: 'change' }
        ]
      };
    },
    tableColumns() {
      return [
        {
          title: this.langMap.name[this.lang],
          key: 'name',
          minWidth: 120,
          render: (h, { row }) => {
            const elements = [];
            
            // 如果是草稿状态，显示草稿标签
            if (row && (row.isTempGraph || row.status === 'draft')) {
              elements.push(
                h('Tag', { 
                  props: { color: 'orange' }, 
                  style: { marginRight: '6px' } 
                }, this.langMap.skeletonGraphDraft[this.lang])
              );
            }
            
            // 显示名称
            elements.push(h('span', row.name));
            
            return h('div', { 
              class: 'text-ellipsis-3', 
              attrs: { title: row.name } 
            }, elements);
          }
        },
        {
          title: this.langMap.belongStation[this.lang],
          key: 'stationName',
          minWidth: 150,
          render: (h, params) => {
            const station = this.stationList.find(s => s.id === params.row.stationId);
            const stationName = station ? station.name : '-';
            return h('span', stationName);
          }
        },
        {
          title: this.langMap.operation[this.lang],
          slot: 'action',
          width: 140,
          align: 'center'
        }
      ];
    }
  },
  mounted() {
    this.calculateHeight();
    window.addEventListener('resize', this.calculateHeight);
    
    // 注册全局方法供弹窗调用
    window.editGraphPoints = this.handleEditPoints;
    window.viewTopology = this.handleViewTopology;

    // 初始化地图
    this.$nextTick(() => {
      this.initMap();
    });

    this.loadGraphList();
    this.loadStationList();
    this.loadFenceData();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateHeight);
    
    // 清理全局方法
    delete window.editGraphPoints;
    delete window.viewTopology;
  },
  methods: {
    // ==================== 地图相关方法 ====================
    
    // 初始化地图
    initMap() {
      if (this.map) return; // 避免重复初始化
      
      const tileLayerOption = this.mapConfig.tileLayerOption;
      this.map = L.map('skeleton_graph_map_id', this.mapConfig.mapInstantiated);
      L.tileLayer(tileLayerOption.url, tileLayerOption).addTo(this.map);

      // 确保地图正确渲染
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
          this.mapLoading = false;
        }
      }, 100);

      // 加载电子围栏
      this.loadFences();
    },

    // 渲染所有骨架图
    // autoZoom: 是否自动缩放地图，默认false（不缩放）
    renderGraphs(autoZoom = false) {
      if (!this.map) return;
      
      // 清除现有图层
      this.clearAllLayers();

      // 编辑模式：渲染所有骨架图（编辑中的正常显示，其他的半透明）
      if (this.editMode && this.editingGraphData) {
        // 先渲染其他骨架图（半透明）
        this.graphList.forEach(graph => {
          if (graph.id !== this.editingGraphData.id) {
            this.createGraphLayer(graph, false); // false表示非编辑状态
          }
        });
        // 最后渲染编辑中的骨架图（正常显示，在最上层）
        this.createGraphLayer(this.editingGraphData, true); // true表示编辑状态
      } else {
        // 非编辑模式：渲染所有骨架图
        this.graphList.forEach(graph => {
          this.createGraphLayer(graph, true); // 非编辑模式下所有图都正常显示
        });
      }

      // 只有在明确要求自动缩放时才执行
      if (autoZoom) {
        if (this.editMode && this.editingGraphData && this.editingGraphData.nodes && this.editingGraphData.nodes.length > 0) {
          this.fitBoundsToGraph(this.editingGraphData.id);
        } else if (this.graphList.length > 0) {
          this.fitBounds();
        }
      }
    },

    // 创建单个骨架图图层
    // isEditing: 是否是正在编辑的骨架图
    createGraphLayer(graph, isEditing = true) {
      if (!graph.nodes || graph.nodes.length === 0) return;

      const graphId = graph.id;
      this.graphLayers[graphId] = L.layerGroup().addTo(this.map);
      this.nodeMarkers[graphId] = [];
      this.edgeLines[graphId] = [];

      // 渲染边
      if (graph.edges && graph.edges.length > 0) {
        this.renderEdges(graph.edges, graphId, graph, isEditing);
      }

      // 渲染节点
      this.renderNodes(graph.nodes, graphId, graph, isEditing);
    },

    // 渲染节点
    renderNodes(nodes, graphId, graph, isEditing = true) {
      nodes.forEach(node => {
        this.createNodeMarker(node, graphId, graph, isEditing);
      });
    },

    // 创建节点标记
    createNodeMarker(node, graphId, graph, isEditing = true) {
      const lat = node.lat || node.latitude;
      const lon = node.lon || node.longitude;
      
      // 根据是否选中或编辑决定颜色
      const isSelected = this.currentGraphId === graphId;
      const isEditingThis = this.editMode && this.editingGraphId === graphId;
      const isSelectedForConnect = this.isManualConnectMode && this.selectedNodeForConnect === node.id;
      const isClickedNode = this.clickedNodeId === node.id; // 是否是被点击查看的节点
      
      const markerDefaultBlue = require('@/assets/images/map/mark_default_blue.png');
      const markerDefaultRed = require('@/assets/images/map/mark_default_red.png');
      const markerDefaultGreen = require('@/assets/images/map/mark_default_green.png');
      const markerChargingBlue = require('@/assets/images/map/mark_charging_blue.png');
      
      let markerImage, labelColor;
      if (isSelectedForConnect) {
        // 手动连线选中的第一个节点：使用充电蓝色图标
        markerImage = markerChargingBlue;
        labelColor = '#1890ff';
      } else if (isClickedNode && isEditingThis) {
        // 编辑模式下被点击查看的节点：使用充电蓝色图标
        markerImage = markerChargingBlue;
        labelColor = '#1890ff';
      } else if (isEditingThis) {
        // 编辑模式：使用绿色图标配紫色标签
        markerImage = markerChargingBlue;
        labelColor = '#9e59a3';
      } else if (isSelected) {
        // 选中状态：使用红色
        markerImage = markerDefaultRed;
        labelColor = '#f5222d';
      } else {
        // 普通状态：使用蓝色
        markerImage = markerDefaultBlue;
        labelColor = '#1890ff';
      }
      
      // 编辑中的骨架图高亮显示，其他骨架图半透明
      const opacity = isEditingThis ? 1 : (isEditing ? 1 : 0.4);
      
      const icon = new L.divIcon({
        html: '<div style="position: relative; width: 15px; height: 15px;">' +
              '<div style="position: absolute; top: -20px; left: 50%; transform: translateX(-50%); white-space: nowrap; font-size: 14px; font-weight: 700; color: ' + labelColor + '; opacity: ' + opacity + ';">' + node.id + '</div>' +
              '<img src="' + markerImage + '" style="width: 15px; height: 15px; opacity: ' + opacity + ';" />' +
              '</div>',
        className: 'my-div-icon',
        iconAnchor: [7, 7],
      });
      
      const marker = L.marker([lat, lon], {
        icon: icon,
        title: `${this.langMap.skeletonGraphNode[this.lang]} ${node.id}`,
        draggable: isEditing && this.editMode && this.editingGraphId === graphId && !this.isManualConnectMode,
        nodeId: node.id,
        graphId: graphId
      });

      marker.addTo(this.graphLayers[graphId]);
      this.nodeMarkers[graphId].push(marker);

      // 只有编辑状态的骨架图才响应点击事件
      if (isEditing) {
        // 点击节点
        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          
          if (this.editMode && this.editingGraphId === graphId) {
            // 编辑模式下：检查是否在手动连线模式
            if (this.isManualConnectMode) {
              // 手动连线模式：直接连接两个选中的节点
              this.handleNodeClickForConnect(node.id);
            } else {
              // 非手动连线模式：设置被点击的节点ID并显示节点编辑弹窗
              this.clickedNodeId = node.id;
              this.showNodeEditPopup(node, marker.getLatLng());
              // 重新渲染地图以更新节点图标
              this.$nextTick(() => {
                this.renderGraphs();
              });
            }
          } else {
            // 非编辑模式：点击节点触发骨架图选中
            this.handleGraphClicked(graphId);
            this.showGraphInfoPopup(graph, e.latlng);
          }
        });

        // 拖动事件
        if (this.editMode && this.editingGraphId === graphId) {
          let dragTimeout = null;
          
          marker.on('drag', () => {
            // 实时更新连线位置
            this.updateEdgesForNode(node.id, graphId);
            
            // 如果有打开的弹窗，更新其位置
            if (this.currentPopup && this.currentPopup.isOpen()) {
              const newLatLng = marker.getLatLng();
              this.currentPopup.setLatLng(newLatLng);
              
              // 使用防抖更新坐标值，避免频繁调用API
              if (dragTimeout) {
                clearTimeout(dragTimeout);
              }
              
              dragTimeout = setTimeout(async () => {
                try {
                  const batchInput = [[newLatLng.lng, newLatLng.lat]];
                  const convertRes = await api.postData(
                    'task-server/coordinate/batchGcj02ToWgs84',
                    batchInput,
                    this
                  );
                  
                  const data = (convertRes && convertRes.success && Array.isArray(convertRes.data))
                    ? convertRes.data
                    : ((convertRes && convertRes.state != 0 && Array.isArray(convertRes.data))
                      ? convertRes.data
                      : null);

                  // 检查data是否为[null]
                  if (data && data.length > 0 && data[0] !== null) {
                    const wgs84Lon = parseFloat(data[0][0]);
                    const wgs84Lat = parseFloat(data[0][1]);
                    
                    // 更新浮窗内容
                    const popupElement = this.currentPopup.getElement();
                    if (popupElement) {
                      const lonInput = popupElement.querySelector('.coord-input[data-field="lon"]');
                      const latInput = popupElement.querySelector('.coord-input[data-field="lat"]');
                      if (lonInput) lonInput.value = wgs84Lon.toFixed(7);
                      if (latInput) latInput.value = wgs84Lat.toFixed(7);
                    }
                  }
                } catch (error) {
                  console.error('拖动时坐标转换失败:', error);
                }
              }, 100); // 100ms防抖
            }
          });
          
          marker.on('dragend', async (e) => {
            const newLatLng = e.target.getLatLng();
            await this.handleNodeDragged({
              graphId: graphId,
              nodeId: node.id,
              lat: newLatLng.lat,
              lng: newLatLng.lng
            });
          });
        }
      }

      return marker;
    },

    // 渲染边
    renderEdges(edges, graphId, graph, isEditing = true) {
      const nodes = graph.nodes;
      const nodeMap = {};
      nodes.forEach(node => {
        nodeMap[node.id] = node;
      });

      edges.forEach(edge => {
        this.createEdgeLine(edge, nodeMap, graphId, isEditing);
      });
    },

    // 创建边线
    createEdgeLine(edge, nodeMap, graphId, isEditing = true) {
      const fromNodeId = edge.fromNodeId || edge.from;
      const toNodeId = edge.toNodeId || edge.to;
      const fromNode = nodeMap[fromNodeId];
      const toNode = nodeMap[toNodeId];

      if (!fromNode || !toNode) return;

      const fromLat = fromNode.lat || fromNode.latitude;
      const fromLon = fromNode.lon || fromNode.longitude;
      const toLat = toNode.lat || toNode.latitude;
      const toLon = toNode.lon || toNode.longitude;

      const points = [
        [fromLat, fromLon],
        [toLat, toLon]
      ];

      // 根据是否选中或编辑决定颜色
      const isSelected = this.currentGraphId === graphId;
      const isEditingThis = this.editMode && this.editingGraphId === graphId;
      const isBidirectional = edge.bidirectional === 1;
      
      let color;
      if (isEditingThis) {
        // 编辑模式：使用黑色系
        color = isBidirectional ? '#000000' : '#424242';
      } else if (isSelected) {
        // 选中状态：使用橙红色系
        color = isBidirectional ? '#f50' : '#ff5722';
      } else {
        // 普通状态：使用蓝色系
        color = isBidirectional ? '#1890ff' : '#ff9800';
      }
      
      // 双向连线使用实线，单向连线使用虚线
      const dashArray = isBidirectional ? null : '10, 5';
      
      // 编辑中的骨架图高亮显示，其他骨架图半透明
      const opacity = isEditingThis ? 0.9 : (isEditing ? (isSelected ? 0.9 : 0.7) : 0.3);
      const weight = isEditingThis ? 4 : (isSelected ? 4 : 3);
      
      const line = L.polyline(points, {
        color: color,
        weight: weight,
        opacity: opacity,
        dashArray: dashArray,
        edgeId: `${fromNodeId}-${toNodeId}`,
        graphId: graphId,
        interactive: isEditing, // 只有编辑状态的连线可交互
        isBidirectional: isBidirectional // 保存双向标记
      });

      line.addTo(this.graphLayers[graphId]);
      this.edgeLines[graphId].push(line);

      // 添加箭头装饰器显示方向
      this.addArrowDecorator(line, isBidirectional, color, graphId, isEditing);

      // 只有编辑状态的连线才响应点击事件
      if (isEditing) {
        // 点击连线
        if (this.editMode && this.editingGraphId === graphId) {
          line.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            this.showEdgePopup(edge, e.latlng);
          });
        } else {
          // 非编辑模式：点击连线也触发骨架图选中
          line.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            const graph = this.graphList.find(g => g.id === graphId);
            if (graph) {
              this.handleGraphClicked(graphId);
              this.showGraphInfoPopup(graph, e.latlng);
            }
          });
        }
      }

      return line;
    },

    // 添加箭头装饰器
    addArrowDecorator(line, isBidirectional, color, graphId, isEditing = true) {
      // 计算线段中点位置
      const latlngs = line.getLatLngs();
      if (latlngs.length < 2) return;

      const start = latlngs[0];
      const end = latlngs[latlngs.length - 1];
      
      // 获取edgeId
      const edgeId = line.options.edgeId;
      
      // 初始化箭头存储
      if (!this.edgeArrows[graphId]) {
        this.edgeArrows[graphId] = {};
      }
      
      // 清除旧箭头
      if (this.edgeArrows[graphId][edgeId]) {
        this.edgeArrows[graphId][edgeId].forEach(arrow => {
          this.graphLayers[graphId].removeLayer(arrow);
        });
      }
      
      this.edgeArrows[graphId][edgeId] = [];
      
      // 计算角度（注意：地图坐标系统中，需要考虑经纬度的实际方向）
      // 使用屏幕坐标来计算角度，而不是地理坐标
      const map = this.map;
      const startPoint = map.latLngToContainerPoint(start);
      const endPoint = map.latLngToContainerPoint(end);
      
      const dx = endPoint.x - startPoint.x;
      const dy = endPoint.y - startPoint.y;
      const angle = Math.atan2(dy, dx) * 180 / Math.PI;
      
      // 非编辑状态的箭头使用半透明
      const opacity = isEditing ? 1 : 0.3;
      
      if (isBidirectional) {
        // 双向连线：在中间位置添加两个相对的箭头，分别在线段上下两侧
        const midLat = (start.lat + end.lat) / 2;
        const midLng = (start.lng + end.lng) / 2;
        
        // 计算偏移（使用CSS transform，不受地图缩放影响）
        const offsetX = 7; // 沿线段方向的偏移
        const offsetY = 0; // 垂直于线段方向的偏移
        
        // 第一个箭头（指向终点，在线段上方）
        const arrow1Icon = L.divIcon({
          html: `<div style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; transform: rotate(${angle}deg) translate(${offsetX}px, -${offsetY}px); opacity: ${opacity};">
                   <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                     <path d="M12 4 L20 12 L12 20 L12 14 L4 14 L4 10 L12 10 Z" fill="${color}" stroke="white" stroke-width="1"/>
                   </svg>
                 </div>`,
          className: 'arrow-decorator',
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });
        
        const arrow1Marker = L.marker([midLat, midLng], {
          icon: arrow1Icon,
          interactive: false,
          keyboard: false
        });
        arrow1Marker.addTo(this.graphLayers[graphId]);
        this.edgeArrows[graphId][edgeId].push(arrow1Marker);
        
        // 第二个箭头（指向起点，在线段下方，旋转180度）
        const arrow2Icon = L.divIcon({
          html: `<div style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; transform: rotate(${angle + 180}deg) translate(${offsetX}px, -${offsetY}px); opacity: ${opacity};">
                   <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                     <path d="M12 4 L20 12 L12 20 L12 14 L4 14 L4 10 L12 10 Z" fill="${color}" stroke="white" stroke-width="1"/>
                   </svg>
                 </div>`,
          className: 'arrow-decorator',
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });
        
        const arrow2Marker = L.marker([midLat, midLng], {
          icon: arrow2Icon,
          interactive: false,
          keyboard: false
        });
        arrow2Marker.addTo(this.graphLayers[graphId]);
        this.edgeArrows[graphId][edgeId].push(arrow2Marker);
      } else {
        // 单向连线：在靠近起点的位置添加一个箭头指向终点
        const arrowLat = start.lat + (end.lat - start.lat) * 0.35;
        const arrowLng = start.lng + (end.lng - start.lng) * 0.35;
        
        const arrowIcon = L.divIcon({
          html: `<div style="width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; transform: rotate(${angle}deg); opacity: ${opacity};">
                   <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                     <path d="M12 4 L20 12 L12 20 L12 14 L4 14 L4 10 L12 10 Z" fill="${color}" stroke="white" stroke-width="1"/>
                   </svg>
                 </div>`,
          className: 'arrow-decorator',
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });
        
        const arrowMarker = L.marker([arrowLat, arrowLng], {
          icon: arrowIcon,
          interactive: false,
          keyboard: false
        });
        arrowMarker.addTo(this.graphLayers[graphId]);
        this.edgeArrows[graphId][edgeId].push(arrowMarker);
      }
    },

    // 显示骨架图信息弹窗（非编辑模式）
    showGraphInfoPopup(graph, latlng) {
      const nodeCount = graph.nodes ? graph.nodes.length : 0;
      const stationName = this.stationList.find(s => s.id === graph.stationId)?.name || '-';
      const description = graph.description || this.langMap.skeletonGraphNoDescription[this.lang];

      const content = `
        <div class="skeleton-graph-popup">
          <h4>${graph.name}</h4>
          <p><strong>${this.langMap.skeletonGraphStationName[this.lang]}：</strong>${stationName}</p>
          <p><strong>${this.langMap.skeletonGraphNodeCount[this.lang]}：</strong>${nodeCount}</p>
          <p><strong>${this.langMap.description[this.lang]}：</strong>${description}</p>
        </div>
      `;

      // 关闭之前的弹窗
      if (this.currentPopup) {
        this.map.closePopup(this.currentPopup);
      }

      const popup = L.popup({ offset: [0, -15] })
        .setLatLng(latlng)
        .setContent(content);

      popup.openOn(this.map);
      this.currentPopup = popup;
    },

    // 显示节点编辑弹窗（编辑模式）
    showNodeEditPopup(node, latlng) {
      const content = `
        <div class="node-edit-popup">
          <h4>${this.langMap.skeletonGraphNode[this.lang]} ${node.id}</h4>
          <div class="coord-row">
            <label>${this.langMap.skeletonGraphWgs84Lon[this.lang]}：</label>
            <input type="number" class="coord-input" data-field="lon" data-node-id="${node.id}" value="${(node.wgs84Lon || node.longitude).toFixed(7)}" step="0.0000001" />
            <i class="ivu-icon ivu-icon-md-checkmark confirm-icon" data-field="lon" data-node-id="${node.id}"></i>
          </div>
          <div class="coord-row">
            <label>${this.langMap.skeletonGraphWgs84Lat[this.lang]}：</label>
            <input type="number" class="coord-input" data-field="lat" data-node-id="${node.id}" value="${(node.wgs84Lat || node.latitude).toFixed(7)}" step="0.0000001" />
            <i class="ivu-icon ivu-icon-md-checkmark confirm-icon" data-field="lat" data-node-id="${node.id}"></i>
          </div>
          <button class="delete-btn" data-node-id="${node.id}">${this.langMap.skeletonGraphDeleteNode[this.lang]}</button>
        </div>
      `;
      
      // 关闭之前的弹窗
      if (this.currentPopup) {
        this.map.closePopup(this.currentPopup);
      }
      
      const popup = L.popup()
        .setLatLng(latlng)
        .setContent(content)
        .openOn(this.map);
      
      this.currentPopup = popup;
      
      // 监听弹窗关闭事件
      popup.on('remove', () => {
        // 清除被点击的节点ID
        this.clickedNodeId = null;
        // 重新渲染地图以恢复节点图标
        this.$nextTick(() => {
          this.renderGraphs();
        });
      });
      
      // 绑定编辑事件
      this.$nextTick(() => {
        this.bindNodeEditPopupEvents(popup, node);
      });
    },

    // 绑定节点编辑弹窗事件
    bindNodeEditPopupEvents(popup, node) {
      const popupElement = popup.getElement();
      if (!popupElement) return;
      
      // 点击确认图标
      const confirmIcons = popupElement.querySelectorAll('.confirm-icon');
      confirmIcons.forEach(icon => {
        icon.addEventListener('click', async (e) => {
          const field = e.target.dataset.field;
          const nodeId = parseInt(e.target.dataset.nodeId);
          const row = e.target.closest('.coord-row');
          const input = row.querySelector('.coord-input');
          const newValue = parseFloat(input.value);
          
          // 验证输入
          if (isNaN(newValue)) {
            this.$Message.error(this.langMap.skeletonGraphInputValidNumber[this.lang]);
            return;
          }
          
          // 验证范围
          if (field === 'lon' && (newValue < -180 || newValue > 180)) {
            this.$Message.error(this.langMap.skeletonGraphLonRangeError[this.lang]);
            return;
          }
          
          if (field === 'lat' && (newValue < -90 || newValue > 90)) {
            this.$Message.error(this.langMap.skeletonGraphLatRangeError[this.lang]);
            return;
          }
          
          // 更新节点坐标
          const updatedNode = { ...node };
          if (field === 'lon') {
            updatedNode.wgs84Lon = newValue;
          } else if (field === 'lat') {
            updatedNode.wgs84Lat = newValue;
          }
          
          // 调用更新方法
          await this.handleUpdateNodeFromPanel(updatedNode);
          
          popup.remove();
        });
      });
      
      // 输入框回车确认
      const inputs = popupElement.querySelectorAll('.coord-input');
      inputs.forEach(input => {
        input.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            const row = e.target.closest('.coord-row');
            const confirmIcon = row.querySelector('.confirm-icon');
            if (confirmIcon) {
              confirmIcon.click();
            }
          }
        });
      });
      
      // 删除按钮
      const deleteBtn = popupElement.querySelector('.delete-btn');
      if (deleteBtn) {
        deleteBtn.addEventListener('click', (e) => {
          const nodeId = parseInt(e.target.dataset.nodeId);
          this.$Modal.confirm({
            title: this.langMap.skeletonGraphDeleteNodeTitle[this.lang],
            content: this.langMap.skeletonGraphDeleteNodeContent[this.lang],
            onOk: () => {
              this.handleDeleteNodeFromPanel(nodeId);
              popup.remove();
            }
          });
        });
      }
    },

    // 显示连线弹窗
    showEdgePopup(edge, latlng) {
      const direction = edge.bidirectional === 1 ? this.langMap.skeletonGraphBidirectional[this.lang] : this.langMap.skeletonGraphUnidirectional[this.lang];
      const distance = edge.weight ? edge.weight.toFixed(2) : this.langMap.skeletonGraphUnknown[this.lang];
      
      const content = `
        <div class="edge-popup">
          <h4>${this.langMap.skeletonGraphEdgeInfo[this.lang]}</h4>
          <p><strong>${this.langMap.skeletonGraphFromNode[this.lang]}：</strong>${this.langMap.skeletonGraphNode[this.lang]}${edge.fromNodeId}</p>
          <p><strong>${this.langMap.skeletonGraphToNode[this.lang]}：</strong>${this.langMap.skeletonGraphNode[this.lang]}${edge.toNodeId}</p>
          <p><strong>${this.langMap.skeletonGraphEdgeDirection[this.lang]}：</strong>${direction}</p>
          <p class="edge-distance"><strong>${this.langMap.skeletonGraphDistance[this.lang]}：</strong>${distance} ${this.langMap.skeletonGraphMeter[this.lang]}</p>
          <button class="delete-btn" data-edge-id="${edge.fromNodeId}-${edge.toNodeId}">${this.langMap.skeletonGraphDeleteEdge[this.lang]}</button>
        </div>
      `;
      
      // 关闭之前的连线弹窗
      if (this.currentEdgePopup) {
        this.map.closePopup(this.currentEdgePopup);
      }
      
      const popup = L.popup()
        .setLatLng(latlng)
        .setContent(content)
        .openOn(this.map);
      
      // 保存当前连线弹窗信息
      this.currentEdgePopup = popup;
      this.currentEdgePopupInfo = {
        edge: edge,
        graphId: this.editingGraphId
      };
      
      // 监听弹窗关闭事件
      popup.on('remove', () => {
        this.currentEdgePopup = null;
        this.currentEdgePopupInfo = null;
      });
      
      // 绑定删除事件
      this.$nextTick(() => {
        const popupElement = popup.getElement();
        if (!popupElement) return;
        
        const deleteBtn = popupElement.querySelector('.delete-btn');
        if (deleteBtn) {
          deleteBtn.addEventListener('click', (e) => {
            const edgeId = e.target.dataset.edgeId;
            this.$Modal.confirm({
              title: this.langMap.skeletonGraphDeleteEdgeTitle[this.lang],
              content: this.langMap.skeletonGraphDeleteEdgeContent[this.lang],
              onOk: () => {
                this.handleDeleteEdgeFromMap({ 
                  graphId: this.editingGraphId, 
                  edgeId 
                });
                popup.remove();
              }
            });
          });
        }
      });
    },

    // 更新节点关联的所有连线
    updateEdgesForNode(nodeId, graphId) {
      if (!this.edgeLines[graphId]) return;
      
      this.edgeLines[graphId].forEach(line => {
        const edgeId = line.options.edgeId;
        const [fromNodeId, toNodeId] = edgeId.split('-');
        
        if (fromNodeId == nodeId || toNodeId == nodeId) {
          const fromMarker = this.nodeMarkers[graphId].find(m => m.options.nodeId == fromNodeId);
          const toMarker = this.nodeMarkers[graphId].find(m => m.options.nodeId == toNodeId);
          
          if (fromMarker && toMarker) {
            // 更新连线位置
            line.setLatLngs([
              fromMarker.getLatLng(),
              toMarker.getLatLng()
            ]);
            
            // 更新箭头位置和角度
            const isBidirectional = line.options.isBidirectional;
            const color = line.options.color;
            const isEditing = this.editMode && this.editingGraphId === graphId;
            this.addArrowDecorator(line, isBidirectional, color, graphId, isEditing);
            
            // 如果有打开的连线弹窗，且正好是这条连线的弹窗，则更新弹窗
            if (this.currentEdgePopup && this.currentEdgePopup.isOpen() && 
                this.currentEdgePopupInfo && this.currentEdgePopupInfo.graphId === graphId) {
              const popupEdge = this.currentEdgePopupInfo.edge;
              const popupEdgeId = `${popupEdge.fromNodeId}-${popupEdge.toNodeId}`;
              
              if (popupEdgeId === edgeId) {
                // 更新弹窗位置到连线中点
                const newCenter = line.getCenter();
                this.currentEdgePopup.setLatLng(newCenter);
                
                // 重新计算距离
                const fromLat = fromMarker.getLatLng().lat;
                const fromLng = fromMarker.getLatLng().lng;
                const toLat = toMarker.getLatLng().lat;
                const toLng = toMarker.getLatLng().lng;
                const newDistance = this.calculateDistance(fromLat, fromLng, toLat, toLng);
                
                // 更新弹窗内容中的距离
                const popupElement = this.currentEdgePopup.getElement();
                if (popupElement) {
                  const distanceElement = popupElement.querySelector('.edge-distance');
                  if (distanceElement) {
                    distanceElement.innerHTML = `<strong>${this.langMap.skeletonGraphDistance[this.lang]}：</strong>${newDistance.toFixed(2)} ${this.langMap.skeletonGraphMeter[this.lang]}`;
                  }
                }
                
                // 更新保存的edge信息中的weight
                this.currentEdgePopupInfo.edge.weight = newDistance;
              }
            }
          }
        }
      });
    },

    // 自动缩放到显示所有图层
    fitBounds() {
      const allPoints = [];
      this.graphList.forEach(graph => {
        if (graph.nodes) {
          graph.nodes.forEach(node => {
            const lat = node.lat || node.latitude;
            const lon = node.lon || node.longitude;
            allPoints.push([lat, lon]);
          });
        }
      });

      if (allPoints.length > 0) {
        const bounds = L.latLngBounds(allPoints);
        this.map.fitBounds(bounds, { padding: [50, 50] });
      }
    },

    // 缩放到指定骨架图
    fitBoundsToGraph(graphId) {
      // 优先从编辑数据中查找（编辑模式）
      let graph = null;
      if (this.editMode && this.editingGraphData && this.editingGraphData.id === graphId) {
        graph = this.editingGraphData;
      } else {
        // 从列表中查找
        graph = this.graphList.find(g => g.id === graphId);
      }
      
      if (!graph || !graph.nodes || graph.nodes.length === 0) return;

      const points = graph.nodes.map(node => {
        const lat = node.lat || node.latitude;
        const lon = node.lon || node.longitude;
        return [lat, lon];
      });

      if (points.length > 0) {
        const bounds = L.latLngBounds(points);
        // 添加maxZoom限制，防止过度放大
        this.map.fitBounds(bounds, { 
          padding: [50, 50],
          maxZoom: 18  // 限制最大缩放级别
        });
      }
    },

    // 清除所有图层
    clearAllLayers() {
      Object.keys(this.graphLayers).forEach(graphId => {
        this.map.removeLayer(this.graphLayers[graphId]);
      });
      this.graphLayers = {};
      this.nodeMarkers = {};
      this.edgeLines = {};
    },

    // 计算两点之间的距离（米）- 使用 Haversine 公式
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371000; // 地球半径（米）
      const dLat = this.toRadians(lat2 - lat1);
      const dLon = this.toRadians(lon2 - lon1);
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;
      return Math.round(distance * 100) / 100; // 保留2位小数
    },

    // 角度转弧度
    toRadians(degrees) {
      return degrees * Math.PI / 180;
    },

    // 加载电子围栏数据
    loadFences() {
      api.getData('task-server/fence/list', this).then((res) => {
        if (res.state != 0 && res.data) {
          this.fenceList = res.data;
          this.initTargetArea(this.fenceList);
        }
      }).catch(() => {
        // 静默失败
      });
    },

    // 初始化绘制区域（电子围栏）
    initTargetArea(areaList) {
      const dataList = areaList || [];
      this.fenceLayers = [];

      dataList.forEach((item) => {
        if (item.area !== null) {
          let color = this.areaColorList[item.type];
          let areas = JSON.parse(item.area);
          let areaTemp = [];
          areas.forEach((item1) => {
            let point = [item1.lat, item1.lon];
            areaTemp.push(point);
          });

          let polygon = L.polygon(areaTemp, {
            color: color,
            id: item.id,
            type: item.type,
            stationId: item.stationId,
            layerPriority: item.layerPriority || 0,
            interactive: false
          }).addTo(this.map);

          this.fenceLayers.push(polygon);
        }
      });
    },

    // ==================== 页面相关方法 ====================
    
    calculateHeight() {
      this.height = window.innerHeight - 141;
      // 通知地图组件更新尺寸
      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      });
    },

    // 加载骨架图列表
    async loadGraphList() {
      this.tableLoading = true; 
      try {
        const res = await api.getData('task-server/skeletonGraph/list', this);
        
        if (res.state != 0) {
          const graphList = res.data || [];
          
          // 批量转换所有骨架图的节点坐标：GCJ02 -> WGS84
          for (const graph of graphList) {
            if (graph.nodes && graph.nodes.length > 0) {
              const batchInput = graph.nodes.map(node => [
                node.longitude || node.lon,
                node.latitude || node.lat
              ]);
              
              try {
                const convertRes = await api.postData(
                  'task-server/coordinate/batchGcj02ToWgs84',
                  batchInput,
                  this
                );
                
                const data = (convertRes && convertRes.success && Array.isArray(convertRes.data))
                  ? convertRes.data
                  : ((convertRes && convertRes.state != 0 && Array.isArray(convertRes.data))
                    ? convertRes.data
                    : null);

                // 检查data是否为[null]或包含null元素
                const isValidData = data && data.length === batchInput.length && !data.some(item => item === null);

                if (isValidData) {
                  // 为每个节点添加WGS84坐标
                  graph.nodes.forEach((node, index) => {
                    node.wgs84Lon = parseFloat(data[index][0]);
                    node.wgs84Lat = parseFloat(data[index][1]);
                  });
                } else {
                  // 数据无效，提示错误
                  this.$Message.error(this.langMap.skeletonGraphCoordConvertError[this.lang]);
                  // 使用原坐标作为WGS84坐标
                  graph.nodes.forEach(node => {
                    node.wgs84Lon = node.longitude || node.lon;
                    node.wgs84Lat = node.latitude || node.lat;
                  });
                }
              } catch (error) {
                console.error('坐标转换失败:', error);
                // 如果转换失败，使用原坐标作为WGS84坐标
                graph.nodes.forEach(node => {
                  node.wgs84Lon = node.longitude || node.lon;
                  node.wgs84Lat = node.latitude || node.lat;
                });
              }
            }
          }
          
          this.graphList = graphList;
          
          // 如果有数据，默认选中第一个
          if (graphList.length > 0) {
            this.currentGraphId = graphList[0].id;
          }
          
          // 渲染地图
          this.$nextTick(() => {
            this.renderGraphs(); // 不自动缩放
            
            // 如果有选中的骨架图，定位到该骨架图
            if (this.currentGraphId) {
              this.focusOnGraph(this.currentGraphId);
            } else if (graphList.length === 0) {
              // 无数据时，显示地图默认中心点
              this.map.setView(this.mapConfig.mapInstantiated.center, this.mapConfig.mapInstantiated.zoom);
            }
          });
        } else {
          this.$Notice.error({
            title: this.langMap.tip[this.lang],
            desc: res.msg
          });
        }
      } catch (error) {
        console.error('加载骨架图列表失败:', error);
      } finally {
        this.tableLoading = false;
      }
    },

    // 处理地图准备就绪
    handleMapReady() {
      this.mapLoading = false;
    },

    // 加载站点列表
    loadStationList() {
      api.getData('task-server/robotStation/list/all', this).then((res) => {
        if (res.state != 0) {
          this.stationList = res.data || [];
        }
      });
    },
    
    // 加载围栏数据（获取站点区域信息）
    async loadFenceData() {
      try {
        const res = await api.getData('task-server/fence/list', this);
        if (res.state != 0) {
          this.fenceList = res.data || [];
        }
      } catch (error) {
        console.error('加载围栏数据失败:', error);
      }
    },
    
    // 处理站点变化
    handleStationChange(stationId) {
      if (!this.map || !stationId) return;
      
      // 检查是否切换了站点（编辑模式下）
      if (this.editMode && this.editingGraphData && !this.isCreatingNew) {
        const originalGraph = this.graphList.find(g => g.id === this.editingGraphData.id);
        if (originalGraph && originalGraph.stationId !== stationId) {
          // 站点发生了变化，标记需要重新分配所有节点ID
          this.stationChanged = true;
        }
      }
      
      // 从围栏数据中查找对应站点的区域信息
      const fence = this.fenceList.find(f => f.stationId === stationId);
      
      if (fence && fence.area) {
        try {
          // 解析area字段（JSON字符串）
          const areaData = typeof fence.area === 'string' ? JSON.parse(fence.area) : fence.area;
          
          if (Array.isArray(areaData) && areaData.length > 0) {
            // 计算区域的中心点和边界
            const lats = areaData.map(point => point.lat);
            const lngs = areaData.map(point => point.lon);
            
            const centerLat = (Math.max(...lats) + Math.min(...lats)) / 2;
            const centerLng = (Math.max(...lngs) + Math.min(...lngs)) / 2;
            
            // 只在编辑模式且没有节点数据时自动定位
            if (this.editMode && this.editingGraphData && 
                (!this.editingGraphData.nodes || this.editingGraphData.nodes.length === 0)) {
              this.map.setView([centerLat, centerLng], 16);
            }
          }
        } catch (error) {
          console.error('解析区域数据失败:', error);
        }
      }
    },

    // 处理数据变更状态
    handleChangeStatus(hasChanges) {
      this.hasUnsavedChanges = hasChanges;
    },

    // 处理连线模式变化
    handleConnectModeChange() {
      // 切换连线模式时，重置连线序列
      this.lastAddedNodeId = null;
    },

    // 创建新骨架图
    createNewGraph() {
      // 清除之前的选中状态
      this.currentGraphId = null;
      
      // 直接进入编辑模式，使用右侧面板
      this.editMode = true;
      this.isCreatingNew = true; // 标记为新增操作
      const tempId = this.tempGraphId++; // 生成临时ID
      this.editingGraphId = tempId; // 使用临时ID而不是null
      this.editingGraphData = {
        id: tempId, // 添加临时ID
        name: '',
        stationId: null,
        description: '',
        nodes: [],
        edges: [],
        config: {
          autoConnect: true,
          connectType: 'manual',
          connectMode: 'insert'
        }
      };
      
      // 重置最后添加的节点ID
      this.lastAddedNodeId = null;
      
      // 重置站点变更标记
      this.stationChanged = false;
      
      // 关闭任何打开的弹窗
      if (this.currentPopup) {
        this.map.closePopup(this.currentPopup);
        this.currentPopup = null;
      }
      
      // 重新渲染地图（新建模式需要自动缩放）
      this.$nextTick(() => {
        this.renderGraphs(true);
      });
    },

    // 确认新建骨架图
    async confirmGraphDialog() {
      this.$refs.graphForm.validate(async (valid) => {
        if (!valid) return;

        // 检查是否有输入数据
        if (!this.jsonInput.trim()) {
          this.$Message.warning(this.langMap.skeletonGraphInputNodeData[this.lang]);
          return;
        }

        // 解析 JSON 数据
        this.parseJSONData(this.jsonInput);

        // 检查解析结果
        if (this.importErrors.length > 0) {
          this.$Notice.error({
            title: this.langMap.tip[this.lang],
            desc: this.importErrors.join('; ')
          });
          return;
        }

        if (this.importedNodes.length < 2) {
          this.$Message.warning(this.langMap.skeletonGraphMinTwoNodesWarning[this.lang]);
          return;
        }

        this.btnLoading = true;

        try {
          // 批量转换坐标：WGS84 -> GCJ02
          // 输入格式：[[lng, lat], [lng, lat], ...]
          const batchInput = this.importedNodes.map(node => [
            parseFloat(node.longitude),
            parseFloat(node.latitude)
          ]);

          const convertRes = await api.postData(
            'task-server/coordinate/batchWgs84ToGcj02',
            batchInput,
            this
          );

          // 判断响应是否成功
          const data = (convertRes && convertRes.success && Array.isArray(convertRes.data))
            ? convertRes.data
            : ((convertRes && convertRes.state != 0 && Array.isArray(convertRes.data))
              ? convertRes.data
              : null);

          // 检查data是否为[null]或包含null元素
          if (!data || data.length !== batchInput.length || data.some(item => item === null)) {
            throw new Error(this.langMap.skeletonGraphCoordConvertError[this.lang]);
          }

          // 更新节点坐标为转换后的 GCJ02 坐标
          // 输出格式：[[lng, lat], [lng, lat], ...]
          const convertedNodes = this.importedNodes.map((node, index) => {
            const lng = Array.isArray(data[index]) ? parseFloat(data[index][0]) : node.longitude;
            const lat = Array.isArray(data[index]) ? parseFloat(data[index][1]) : node.latitude;
            return {
              ...node,
              longitude: lng,
              latitude: lat
            };
          });

          // 创建临时骨架图对象（未保存到后端）
          const stationName = this.stationList.find(s => s.id === this.graphForm.stationId)?.name || '';
          const tempGraph = {
            id: this.tempGraphId++, // 使用临时ID
            name: this.graphForm.name,
            stationId: this.graphForm.stationId,
            stationName: stationName,
            description: this.graphForm.description || '',
            nodes: convertedNodes,
            edges: [], // 初始为空，需要在地图上绘制
            isTempGraph: true, // 标记为临时数据
            status: 'draft' // 草稿状态
          };

          // 添加到列表
          this.graphList.unshift(tempGraph);
          
          // 选中新创建的骨架图并在地图上显示
          this.currentGraphId = tempGraph.id;
          this.$nextTick(() => {
            if (this.$refs.map) {
              this.$refs.map.focusOnGraph(tempGraph.id);
            }
          });
          
          this.$Message.success(this.langMap.skeletonGraphCreatedSuccess[this.lang]);
          this.graphDialogVisible = false;
          
          // 清空表单
          this.$refs.graphForm.resetFields();
          this.importedNodes = [];
          this.importedEdges = [];
          this.jsonInput = '';
          this.importErrors = [];
          this.graphForm.description = '';
        } catch (error) {
          this.$Notice.error({
            title: this.langMap.tip[this.lang],
            desc: error.message || this.langMap.skeletonGraphCoordConvertError[this.lang]
          });
        } finally {
          this.btnLoading = false;
        }
      });
    },

    // 取消对话框
    cancelGraphDialog() {
      this.graphDialogVisible = false;
      this.$refs.graphForm.resetFields();
      this.importedNodes = [];
      this.importedEdges = [];
      this.jsonInput = '';
      this.importErrors = [];
      this.graphForm.description = '';
    },

    // 显示导入对话框
    showImportDialog() {
      this.importDialogVisible = true;
    },

    // 处理导入成功
    async handleImportSuccess(data) {
      // 如果在编辑模式下，将导入的节点添加到编辑数据中
      if (this.editMode && this.editingGraphData) {
        // 生成新的节点ID
        const maxId = this.editingGraphData.nodes.length > 0
          ? Math.max(...this.editingGraphData.nodes.map(n => parseInt(n.id) || 0))
          : 0;
        
        // 为导入的节点分配新ID并转换坐标
        const newNodes = [];
        for (let i = 0; i < data.nodes.length; i++) {
          const node = data.nodes[i];
          node.id = maxId + i + 1;
          // 假设导入的坐标是WGS84格式
          node.wgs84Lon = node.longitude || node.lon;
          node.wgs84Lat = node.latitude || node.lat;
          newNodes.push(node);
        }
        
        // 批量转换坐标：WGS84 -> GCJ02
        try {
          const batchInput = newNodes.map(node => [node.wgs84Lon, node.wgs84Lat]);
          
          const convertRes = await api.postData(
            'task-server/coordinate/batchWgs84ToGcj02',
            batchInput,
            this
          );
          
          const convertData = (convertRes && convertRes.success && Array.isArray(convertRes.data))
            ? convertRes.data
            : ((convertRes && convertRes.state != 0 && Array.isArray(convertRes.data))
              ? convertRes.data
              : null);

          // 检查data是否为[null]或包含null元素
          const isValidData = convertData && convertData.length === batchInput.length && !convertData.some(item => item === null);

          if (isValidData) {
            // 更新节点坐标为GCJ02
            newNodes.forEach((node, index) => {
              node.longitude = parseFloat(convertData[index][0]);
              node.latitude = parseFloat(convertData[index][1]);
              node.lon = node.longitude;
              node.lat = node.latitude;
            });
          }
        } catch (error) {
          console.error('坐标转换失败:', error);
          // 如果转换失败，使用原坐标
          newNodes.forEach(node => {
            node.longitude = node.wgs84Lon;
            node.latitude = node.wgs84Lat;
            node.lon = node.longitude;
            node.lat = node.latitude;
          });
        }
        
        // 添加到节点列表
        this.editingGraphData.nodes.push(...newNodes);
        
        // 根据连线配置创建连线（只要开启了自动连线就执行）
        const config = this.editingGraphData.config || {};
        
        if (config.autoConnect) {
          const mode = config.connectMode || 'insert';
          
          if (mode === 'sequential') {
            // 顺序连接：新添加的节点之间按ID顺序连接，不与已有节点连接
            // 对新节点按ID排序
            const sortedNewNodes = [...newNodes].sort((a, b) => a.id - b.id);
            
            if (!this.editingGraphData.edges) {
              this.editingGraphData.edges = [];
            }
            
            // 只连接新节点之间
            for (let i = 0; i < sortedNewNodes.length - 1; i++) {
              const currentNode = sortedNewNodes[i];
              const nextNode = sortedNewNodes[i + 1];
              
              const distance = autoConnector.calculateDistance(
                currentNode.latitude || currentNode.lat,
                currentNode.longitude || currentNode.lon,
                nextNode.latitude || nextNode.lat,
                nextNode.longitude || nextNode.lon
              );
              
              this.editingGraphData.edges.push({
                fromNodeId: currentNode.id,
                toNodeId: nextNode.id,
                bidirectional: 1,
                weight: distance
              });
            }
          } else {
            // 插入模式或最近邻模式：为每个新节点创建连线
            newNodes.forEach(newNode => {
              const existingNodes = this.editingGraphData.nodes.filter(n => n.id !== newNode.id);
              let newEdge = null;
              
              if (mode === 'insert') {
                newEdge = autoConnector.insertConnect(existingNodes, newNode);
              } else if (mode === 'nearest') {
                newEdge = autoConnector.nearestNeighborConnect(existingNodes, newNode);
              }
              
              if (newEdge) {
                if (!this.editingGraphData.edges) {
                  this.editingGraphData.edges = [];
                }
                this.editingGraphData.edges.push(newEdge);
              }
            });
          }
        }
        // 如果是manual模式或未开启自动连线，不自动创建连线
        
        // 重新渲染地图并自动定位到导入的节点
        this.$nextTick(() => {
          this.renderGraphs();
          // 导入后自动缩放到显示所有节点
          if (this.editingGraphData && this.editingGraphData.id) {
            this.fitBoundsToGraph(this.editingGraphData.id);
          }
        });
        
        this.$Message.success(this.langMap.skeletonGraphImportNodesSuccessMsg[this.lang].replace('{count}', newNodes.length));
      } else {
        // 非编辑模式下的导入（原有逻辑）
        this.importedNodes = data.nodes;
        this.importedEdges = data.edges;
        this.$Message.success(this.langMap.skeletonGraphImportSuccess[this.lang]);
      }
    },

    // 文件上传前验证
    beforeUpload(file) {
      // 验证文件类型
      if (!file.name.endsWith('.json')) {
        this.$Message.error(this.langMap.skeletonGraphFileFormatError[this.lang]);
        return false;
      }

      // 验证文件大小
      if (file.size > 10 * 1024 * 1024) {
        this.$Message.error(this.langMap.skeletonGraphFileSizeLimit[this.lang]);
        return false;
      }

      // 读取文件内容
      const reader = new FileReader();
      reader.onload = (e) => {
        this.jsonInput = e.target.result;
        this.parseJSONData(this.jsonInput);
      };
      reader.readAsText(file);

      return false; // 阻止自动上传
    },

    // 格式化 JSON
    formatJSON() {
      if (!this.jsonInput.trim()) {
        this.$Message.warning(this.langMap.skeletonGraphJsonRequired[this.lang]);
        return;
      }

      const { formatJSONString } = require('@/utils/skeletonGraph/dataParser');
      try {
        this.jsonInput = formatJSONString(this.jsonInput);
        this.$Message.success(this.langMap.formatSuccess[this.lang]);
      } catch (e) {
        this.$Message.error(e.message);
      }
    },

    // 从剪贴板粘贴
    async pasteFromClipboard() {
      try {
        const text = await navigator.clipboard.readText();
        this.jsonInput = text;
        this.$Message.success(this.langMap.skeletonGraphPasteSuccess[this.lang]);
      } catch (e) {
        this.$Message.error(this.langMap.skeletonGraphPasteFailed[this.lang]);
      }
    },

    // 清空 JSON
    clearJSON() {
      this.jsonInput = '';
      this.importedNodes = [];
      this.importedEdges = [];
      this.importErrors = [];
    },

    // 处理 JSON 输入变化（自动解析）
    handleJsonInputChange() {
      // 清除之前的定时器
      if (this.parseTimer) {
        clearTimeout(this.parseTimer);
      }
      
      // 如果输入为空，清空导入结果
      if (!this.jsonInput.trim()) {
        this.importedNodes = [];
        this.importedEdges = [];
        this.importErrors = [];
        return;
      }
      
      // 设置新的定时器，延迟 500ms 后自动解析
      this.parseTimer = setTimeout(() => {
        this.parseJSONData(this.jsonInput);
      }, 500);
    },

    // 解析 JSON 数据
    parseJSONData(jsonString) {
      const { parseJSON } = require('@/utils/skeletonGraph/dataParser');
      const result = parseJSON(jsonString);
      
      if (result.errors.length === 0) {
        this.importedNodes = result.nodes;
        this.importedEdges = result.edges;
        this.importErrors = [];
      } else {
        this.importErrors = result.errors;
        this.importedNodes = [];
        this.importedEdges = [];
      }
    },

    // 处理表格行点击
    handleRowClick(row) {
      this.currentGraphId = row.id;
      // 重新渲染地图以更新颜色（不自动缩放）
      this.renderGraphs();
      // 定位到骨架图（表格点击需要缩放）
      this.fitBoundsToGraph(row.id);
    },

    // 获取表格行样式
    rowClassName(row, index) {
      return row.id === this.currentGraphId ? 'table-row-selected' : '';
    },

    // 处理地图上骨架图被点击
    handleGraphClicked(graphId) {
      this.currentGraphId = graphId;
      this.focusOnGraphInTable(graphId);
      // 重新渲染地图以更新颜色（点击地图元素不缩放）
      this.renderGraphs();
    },

    // 在表格中定位并高亮指定骨架图
    focusOnGraphInTable(graphId) {
      const index = this.graphList.findIndex(g => g.id === graphId);
      if (index !== -1) {
        // 滚动到对应行
        this.$nextTick(() => {
          const tableBody = document.querySelector('.ivu-table-body');
          if (tableBody) {
            const rows = tableBody.querySelectorAll('tr');
            if (rows[index]) {
              rows[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        });
      }
    },
    
    // 定位到指定骨架图
    focusOnGraph(graphId) {
      this.fitBoundsToGraph(graphId);
    },

    // 处理编辑连线
    handleEditEdges(graphId) {
      const graph = this.graphList.find(g => g.id === graphId);
      if (!graph) {
        this.$Message.error(this.langMap.skeletonGraphNotFound[this.lang]);
        return;
      }

      // 关闭所有弹窗
      if (this.currentPopup) {
        this.map.closePopup(this.currentPopup);
        this.currentPopup = null;
      }

      // 清除之前的选中状态
      this.currentGraphId = null;

      // 进入编辑模式
      this.editMode = true;
      this.isCreatingNew = false; // 标记为编辑操作
      this.editingGraphId = graphId;
      this.editingGraphData = JSON.parse(JSON.stringify(graph)); // 深拷贝
      
      // 重置最后添加的节点ID
      this.lastAddedNodeId = null;
      
      // 重置站点变更标记
      this.stationChanged = false;
      
      // 确保config对象存在
      if (!this.editingGraphData.config) {
        this.editingGraphData.config = {
          autoConnect: true,
          connectType: 'manual',
          connectMode: 'insert'
        };
      }
      // 确保所有必需字段存在
      if (this.editingGraphData.config.autoConnect === undefined) {
        this.editingGraphData.config.autoConnect = true;
      }
      if (!this.editingGraphData.config.connectType) {
        this.editingGraphData.config.connectType = 'manual';
      }
      if (!this.editingGraphData.config.connectMode) {
        this.editingGraphData.config.connectMode = 'insert';
      }
      
      // 确保nodes和edges数组存在
      if (!this.editingGraphData.nodes) {
        this.editingGraphData.nodes = [];
      }
      if (!this.editingGraphData.edges) {
        this.editingGraphData.edges = [];
      }
      
      // 为每个节点添加wgs84坐标字段（如果不存在）
      this.editingGraphData.nodes.forEach(node => {
        if (!node.wgs84Lon) {
          node.wgs84Lon = node.longitude || node.lon;
        }
        if (!node.wgs84Lat) {
          node.wgs84Lat = node.latitude || node.lat;
        }
      });
      
      // 重新渲染地图（进入编辑模式需要自动缩放）
      this.$nextTick(() => {
        this.renderGraphs(true);
      });

      this.$Message.info(this.langMap.skeletonGraphEditModeInfo[this.lang]);
    },

    // 使节点可拖动
    makeNodesDraggable(graphId) {
      if (!this.nodeMarkers[graphId]) return;

      this.nodeMarkers[graphId].forEach(marker => {
        marker.dragging.enable();
      });
    },

    // 处理编辑点位（保留旧方法名以兼容）
    handleEditPoints(graphId) {
      this.handleEditEdges(graphId);
    },

    // 退出编辑模式
    exitEditMode() {
      this.editMode = false;
      this.isCreatingNew = false; // 重置新增标志
      this.editingGraphId = null;
      this.editingGraphData = null;
      this.selectedNodeForConnect = null; // 重置选中节点
      this.clickedNodeId = null; // 重置被点击的节点
      
      // 关闭所有打开的弹窗
      if (this.currentPopup) {
        this.map.closePopup(this.currentPopup);
        this.currentPopup = null;
      }
      
      // 关闭连线弹窗
      if (this.currentEdgePopup) {
        this.map.closePopup(this.currentEdgePopup);
        this.currentEdgePopup = null;
        this.currentEdgePopupInfo = null;
      }
      
      // 清除地图上的所有骨架图图层（包括临时的）
      Object.keys(this.graphLayers).forEach(graphId => {
        if (this.graphLayers[graphId]) {
          this.map.removeLayer(this.graphLayers[graphId]);
        }
      });
      this.graphLayers = {};
      this.nodeMarkers = {};
      this.edgeLines = {};
      this.edgeArrows = {};
      
      // 退出地图添加模式
      if (this.isMapAddNodeMode) {
        this.isMapAddNodeMode = false;
        if (this.mapClickHandler) {
          this.map.off('click', this.mapClickHandler);
          this.mapClickHandler = null;
        }
      }
      
      // 退出手动连线模式
      if (this.isManualConnectMode) {
        this.exitManualConnectMode();
      }

      // 通知地图组件退出编辑模式
      if (this.$refs.map) {
        this.$refs.map.disableEditMode();
      }
    },

    // 保存编辑
    async saveEdit() {
      if (!this.editingGraphData) return;

      // 检查是否有边数据
      if (!this.editingGraphData.edges || this.editingGraphData.edges.length === 0) {
        this.$Message.warning(this.langMap.skeletonGraphMinOneEdgeWarning[this.lang]);
        return;
      }

      // 验证拓扑图
      const validation = validateTopology(
        this.editingGraphData.nodes,
        this.editingGraphData.edges
      );

      if (!validation.valid) {
        this.$Notice.error({
          title: this.langMap.tip[this.lang],
          desc: validation.errors.join('; ')
        });
        return;
      }

      // 检查连通性
      const isConnected = isGraphConnected(
        this.editingGraphData.nodes,
        this.editingGraphData.edges
      );

      if (!isConnected) {
        this.$Modal.error({
          title: this.langMap.topoValidatorGraphNotConnected[this.lang],
          content: this.langMap.topoValidatorGraphNotConnectedDesc[this.lang]
        });
        return;
      }

      this.btnLoading = true;

      try {
        // 获取最大节点ID（需要传递stationId）
        const maxIdRes = await api.getData(
          `task-server/skeletonGraph/getMaxNodeId?stationId=${this.editingGraphData.stationId}`, 
          this
        );
        let maxNodeId = 0;
        
        if (maxIdRes && maxIdRes.state != 0 && typeof maxIdRes.data === 'number') {
          maxNodeId = maxIdRes.data;
        }
        
        let nodesWithNewIds;
        let edgesWithNewIds;
        
        if (this.isCreatingNew) {
          // 新增模式：所有节点都重新分配ID
          const nodeIdMap = {};
          let currentMaxId = maxNodeId;
          
          nodesWithNewIds = this.editingGraphData.nodes.map(node => {
            currentMaxId++;
            nodeIdMap[node.id] = currentMaxId;
            return {
              ...node,
              id: currentMaxId
            };
          });
          
          // 更新edges中的节点ID引用
          edgesWithNewIds = this.editingGraphData.edges.map(edge => {
            return {
              ...edge,
              fromNodeId: nodeIdMap[edge.fromNodeId] || edge.fromNodeId,
              toNodeId: nodeIdMap[edge.toNodeId] || edge.toNodeId
            };
          });
        } else if (this.stationChanged) {
          // 编辑模式但切换了站点：所有节点都重新分配ID
          const nodeIdMap = {};
          let currentMaxId = maxNodeId;
          
          nodesWithNewIds = this.editingGraphData.nodes.map(node => {
            currentMaxId++;
            nodeIdMap[node.id] = currentMaxId;
            return {
              ...node,
              id: currentMaxId
            };
          });
          
          // 更新edges中的节点ID引用
          edgesWithNewIds = this.editingGraphData.edges.map(edge => {
            return {
              ...edge,
              fromNodeId: nodeIdMap[edge.fromNodeId] || edge.fromNodeId,
              toNodeId: nodeIdMap[edge.toNodeId] || edge.toNodeId
            };
          });
          
          // 重置站点变更标记
          this.stationChanged = false;
        } else {
          // 编辑模式且未切换站点：只为新增的节点分配新ID，原有节点保持原ID
          // 从原始数据中获取已存在的节点ID列表
          const originalGraph = this.graphList.find(g => g.id === this.editingGraphData.id);
          const originalNodeIds = originalGraph && originalGraph.nodes 
            ? originalGraph.nodes.map(n => n.id) 
            : [];
          
          const nodeIdMap = {};
          let currentMaxId = maxNodeId;
          
          nodesWithNewIds = this.editingGraphData.nodes.map(node => {
            // 如果是原有节点，保持原ID
            if (originalNodeIds.includes(node.id)) {
              nodeIdMap[node.id] = node.id;
              return node;
            }
            
            // 如果是新增节点，分配新ID
            currentMaxId++;
            nodeIdMap[node.id] = currentMaxId;
            return {
              ...node,
              id: currentMaxId
            };
          });
          
          // 更新edges中的节点ID引用
          edgesWithNewIds = this.editingGraphData.edges.map(edge => {
            return {
              ...edge,
              fromNodeId: nodeIdMap[edge.fromNodeId] || edge.fromNodeId,
              toNodeId: nodeIdMap[edge.toNodeId] || edge.toNodeId
            };
          });
        }
        
        // 批量转换坐标：WGS84 -> GCJ02
        const batchInput = nodesWithNewIds.map(node => [
          node.wgs84Lon || node.longitude,
          node.wgs84Lat || node.latitude
        ]);
        
        const convertRes = await api.postData(
          'task-server/coordinate/batchWgs84ToGcj02',
          batchInput,
          this
        );
        
        const data = (convertRes && convertRes.success && Array.isArray(convertRes.data))
          ? convertRes.data
          : ((convertRes && convertRes.state != 0 && Array.isArray(convertRes.data))
            ? convertRes.data
            : null);

        // 检查data是否为[null]或包含null元素
        if (!data || data.length !== batchInput.length || data.some(item => item === null)) {
          throw new Error(this.langMap.skeletonGraphCoordConvertError[this.lang]);
        }
        
        // 更新节点坐标为GCJ02，并清理多余字段
        const cleanedNodes = nodesWithNewIds.map((node, index) => {
          return {
            id: node.id,
            longitude: parseFloat(data[index][0]),
            latitude: parseFloat(data[index][1])
          };
        });

        // 保存到后端
        const saveData = {
          name: this.editingGraphData.name,
          stationId: this.editingGraphData.stationId,
          description: this.editingGraphData.description || '',
          nodes: cleanedNodes,
          edges: edgesWithNewIds
          // config字段不需要保存到后端，仅用于前端编辑行为控制
        };

        // 根据操作类型判断使用哪个接口
        let apiUrl;
        if (this.isCreatingNew) {
          // 新增骨架图：使用 /save 接口，不传 id
          apiUrl = 'task-server/skeletonGraph/save';
        } else {
          // 更新骨架图：使用 /update/{id} 接口，id 放在 URL 中
          apiUrl = `task-server/skeletonGraph/update/${this.editingGraphData.id}`;
        }

        const saveRes = await api.postData(apiUrl, saveData, this);
        
        if (saveRes.state != 0) {
          this.$Message.success(this.langMap.saveSuccess[this.lang]);
          this.exitEditMode();
          this.loadGraphList();
        } else {
          throw new Error(saveRes.msg);
        }
      } catch (error) {
        this.$Notice.error({
          title: this.langMap.tip[this.lang],
          desc: error.message
        });
      } finally {
        this.btnLoading = false;
      }
    },

    // 取消编辑
    cancelEdit() {
      // 如果没有未保存的修改，直接退出
      if (!this.hasUnsavedChanges) {
        this.exitEditMode();
        this.loadGraphList();
        return;
      }
      
      // 有未保存的修改，显示确认框
      this.$Modal.confirm({
        title: this.langMap.confirm[this.lang],
        content: this.langMap.cancelEditConfirm[this.lang],
        onOk: () => {
          this.exitEditMode();
          this.loadGraphList();
        }
      });
    },

    // 处理查看拓扑图
    handleViewTopology(graphId) {
      const graph = this.graphList.find(g => g.id === graphId);
      if (graph) {
        this.currentTopologyData = {
          nodes: graph.nodes || [],
          edges: graph.edges || []
        };
        this.topologyDialogVisible = true;
      }
    },

    // 处理节点点击
    handleNodeClick(data) {
      if (this.editMode && this.editingGraphData) {
        // 编辑模式下的节点点击由地图组件处理
      }
    },

    // 处理边创建
    handleEdgeCreated(edge) {
      if (this.editMode && this.editingGraphData) {
        // 添加到编辑数据中
        this.editingGraphData.edges.push(edge);
        this.$Message.success(this.langMap.skeletonGraphEdgeCreated[this.lang]);
      }
    },

    // 处理批量边创建
    handleEdgesCreated(edges) {
      if (this.editMode && this.editingGraphData) {
        // 添加到编辑数据中
        this.editingGraphData.edges.push(...edges);
        this.$Message.success(
          this.langMap.skeletonGraphSmartPathCreated[this.lang] + 
          ` (${edges.length}${this.langMap.skeletonGraphEdgesCount[this.lang]})`
        );
      }
    },

    // 处理节点位置更新
    handleNodePositionUpdated(data) {
      if (this.editMode && this.editingGraphData) {
        // 更新编辑数据中的节点位置
        const node = this.editingGraphData.nodes.find(n => n.id === data.nodeId);
        if (node) {
          node.lat = data.lat;
          node.lon = data.lon;
        }
      }
    },

    // 处理边删除
    handleEdgeDeleted(edgeId) {
      if (this.editMode && this.editingGraphData) {
        // 从编辑数据中删除
        const index = this.editingGraphData.edges.findIndex(e => {
          const fromNodeId = e.fromNodeId || e.from;
          const toNodeId = e.toNodeId || e.to;
          return edgeId === `${fromNodeId}-${toNodeId}`;
        });
        
        if (index !== -1) {
          this.editingGraphData.edges.splice(index, 1);
          this.$Message.success(this.langMap.skeletonGraphEdgeDeleted[this.lang]);
        }
      }
    },

    // 处理删除按钮点击
    handleDeleteClick(graphId) {
      this.$Modal.confirm({
        title: this.langMap.skeletonGraphDeleteGraphTitle[this.lang],
        content: this.langMap.skeletonGraphDeleteGraphContent[this.lang],
        onOk: () => {
          this.deleteGraph(graphId);
        }
      });
    },

    // 删除骨架图
    deleteGraph(graphId) {
      const graph = this.graphList.find(g => g.id === graphId);
      
      // 如果是临时数据，直接从列表中删除
      if (graph && graph.isTempGraph) {
        this.graphList.splice(this.graphList.findIndex(g => g.id === graphId), 1);
        this.$Message.success(this.langMap.deleteSuccess[this.lang]);
        if (this.currentGraphId === graphId) {
          this.currentGraphId = null;
        }
        return;
      }

      // 已保存的数据，调用后端删除接口
      api.postData(`task-server/skeletonGraph/delete?id=${graphId}`, {}, this).then((res) => {
        if (res.state != 0) {
          this.$Message.success(this.langMap.deleteSuccess[this.lang]);
          this.loadGraphList();
          // 如果删除的是当前选中的骨架图，清空选中状态
          if (this.currentGraphId === graphId) {
            this.currentGraphId = null;
          }
        } else {
          this.$Notice.error({
            title: this.langMap.tip[this.lang],
            desc: res.msg
          });
        }
      });
    },

    // 处理编辑面板的数据更新
    handleGraphDataUpdate(graphData) {
      this.editingGraphData = graphData;
    },

    // 处理地图添加节点按钮点击
    handleMapAddNodeClick() {
      // 如果在手动连线模式，先退出
      if (this.isManualConnectMode) {
        this.exitManualConnectMode();
      }
      
      if (this.isMapAddNodeMode) {
        // 如果已经在添加模式，取消模式
        this.isMapAddNodeMode = false;
        this.map.off('click', this.mapClickHandler);
        this.lastAddedNodeId = null; // 重置最后添加的节点ID
        this.$Message.info(this.langMap.skeletonGraphCancelMapAddModeMsg[this.lang]);
      } else {
        // 进入添加模式
        this.isMapAddNodeMode = true;
        this.lastAddedNodeId = null; // 重置最后添加的节点ID
        this.$Message.info(this.langMap.skeletonGraphMapAddModeMsg[this.lang]);
        
        // 保存点击处理器引用以便后续移除
        this.mapClickHandler = async (e) => {
          await this.handleMapClicked(e.latlng);
        };
        
        // 添加地图点击事件监听（持续监听）
        this.map.on('click', this.mapClickHandler);
      }
    },
    
    // 处理手动连线按钮点击
    handleManualConnectClick() {
      // 如果在地图添加模式，先退出
      if (this.isMapAddNodeMode) {
        this.isMapAddNodeMode = false;
        if (this.mapClickHandler) {
          this.map.off('click', this.mapClickHandler);
          this.mapClickHandler = null;
        }
      }
      
      if (this.isManualConnectMode) {
        // 退出手动连线模式
        this.exitManualConnectMode();
        this.$Message.info(this.langMap.skeletonGraphExitManualConnectMode[this.lang]);
      } else {
        // 进入手动连线模式
        this.isManualConnectMode = true;
        this.selectedNodeForConnect = null;
        this.$Message.info(this.langMap.skeletonGraphManualConnectModeOn[this.lang]);
      }
    },
    
    // 退出手动连线模式
    exitManualConnectMode() {
      this.isManualConnectMode = false;
      this.selectedNodeForConnect = null;
      // 清除所有节点的高亮状态（不缩放）
      this.renderGraphs();
    },
    
    // 处理手动连线时的节点点击
    handleNodeClickForConnect(nodeId) {
      if (!this.isManualConnectMode || !this.editingGraphData) return;
      
      if (!this.selectedNodeForConnect) {
        // 选择第一个节点
        this.selectedNodeForConnect = nodeId;
        // 重新渲染以高亮选中的节点（不缩放）
        this.renderGraphs();
      } else {
        // 选择第二个节点，创建连线
        const fromNodeId = this.selectedNodeForConnect;
        const toNodeId = nodeId;
        
        if (fromNodeId === toNodeId) {
          this.$Message.warning(this.langMap.skeletonGraphCannotConnectSame[this.lang]);
          return;
        }
        
        // 检查智能连线是否开启
        const autoConnect = this.editingGraphData.config?.autoConnect || false;
        
        if (autoConnect) {
          // 智能连线开启：连接ID范围内的所有节点
          const minId = Math.min(fromNodeId, toNodeId);
          const maxId = Math.max(fromNodeId, toNodeId);
          
          // 获取ID范围内的所有节点并排序
          const nodesInRange = this.editingGraphData.nodes
            .filter(n => n.id >= minId && n.id <= maxId)
            .sort((a, b) => a.id - b.id);
          
          if (nodesInRange.length < 2) {
            this.$Message.warning(this.langMap.skeletonGraphNotEnoughNodes[this.lang]);
            this.selectedNodeForConnect = null;
            return;
          }
          
          // 检查是否所有连线都已存在
          let hasNewEdge = false;
          const newEdges = [];
          
          for (let i = 0; i < nodesInRange.length - 1; i++) {
            const currentNodeId = nodesInRange[i].id;
            const nextNodeId = nodesInRange[i + 1].id;
            
            // 检查连线是否已存在
            const existingEdge = this.editingGraphData.edges.find(edge => {
              return (edge.fromNodeId === currentNodeId && edge.toNodeId === nextNodeId) ||
                     (edge.fromNodeId === nextNodeId && edge.toNodeId === currentNodeId);
            });
            
            if (!existingEdge) {
              hasNewEdge = true;
              
              // 计算距离
              const currentNode = nodesInRange[i];
              const nextNode = nodesInRange[i + 1];
              
              const distance = autoConnector.calculateDistance(
                currentNode.latitude || currentNode.lat,
                currentNode.longitude || currentNode.lon,
                nextNode.latitude || nextNode.lat,
                nextNode.longitude || nextNode.lon
              );
              
              newEdges.push({
                fromNodeId: currentNodeId,
                toNodeId: nextNodeId,
                bidirectional: 1,
                weight: distance
              });
            }
          }
          
          if (!hasNewEdge) {
            this.$Message.warning(this.langMap.skeletonGraphAllEdgesExist[this.lang]);
            this.selectedNodeForConnect = null;
            this.renderGraphs();
            return;
          }
          
          // 添加新连线
          if (!this.editingGraphData.edges) {
            this.editingGraphData.edges = [];
          }
          
          this.editingGraphData.edges.push(...newEdges);
        } else {
          // 智能连线关闭：只连接两个选中的节点
          // 检查连线是否已存在
          const existingEdge = this.editingGraphData.edges.find(edge => {
            return (edge.fromNodeId === fromNodeId && edge.toNodeId === toNodeId) ||
                   (edge.fromNodeId === toNodeId && edge.toNodeId === fromNodeId);
          });
          
          if (existingEdge) {
            this.$Message.warning(this.langMap.skeletonGraphEdgeAlreadyExists[this.lang]);
            this.selectedNodeForConnect = null;
            this.renderGraphs();
            return;
          }
          
          // 获取节点信息
          const fromNode = this.editingGraphData.nodes.find(n => n.id === fromNodeId);
          const toNode = this.editingGraphData.nodes.find(n => n.id === toNodeId);
          
          if (!fromNode || !toNode) {
            this.$Message.error(this.langMap.skeletonGraphNodeNotExist[this.lang]);
            this.selectedNodeForConnect = null;
            return;
          }
          
          // 计算距离
          const distance = autoConnector.calculateDistance(
            fromNode.latitude || fromNode.lat,
            fromNode.longitude || fromNode.lon,
            toNode.latitude || toNode.lat,
            toNode.longitude || toNode.lon
          );
          
          // 创建新连线
          const newEdge = {
            fromNodeId: fromNodeId,
            toNodeId: toNodeId,
            bidirectional: 1,
            weight: distance
          };
          
          if (!this.editingGraphData.edges) {
            this.editingGraphData.edges = [];
          }
          
          this.editingGraphData.edges.push(newEdge);
        }
        
        // 清除选择状态
        this.selectedNodeForConnect = null;
        
        // 重新渲染地图（手动连线不缩放）
        this.$nextTick(() => {
          this.renderGraphs();
        });
      }
    },

    // 处理从编辑面板删除节点
    handleDeleteNodeFromPanel(nodeId) {
      if (!this.editingGraphData) return;
      
      // 从节点列表中删除
      const nodeIndex = this.editingGraphData.nodes.findIndex(n => n.id === nodeId);
      if (nodeIndex !== -1) {
        this.editingGraphData.nodes.splice(nodeIndex, 1);
      }
      
      // 删除所有关联的连线
      if (this.editingGraphData.edges) {
        this.editingGraphData.edges = this.editingGraphData.edges.filter(edge => {
          const fromNodeId = edge.fromNodeId || edge.from;
          const toNodeId = edge.toNodeId || edge.to;
          return fromNodeId !== nodeId && toNodeId !== nodeId;
        });
      }
      
      // 重新渲染地图（删除节点不缩放）
      this.$nextTick(() => {
        this.renderGraphs();
      });
      
      this.$Message.success(this.langMap.skeletonGraphNodeDeleted[this.lang]);
    },

    // 处理从编辑面板更新节点
    async handleUpdateNodeFromPanel(node) {
      if (!this.editingGraphData) return;
      
      try {
        // 批量转换坐标：WGS84 -> GCJ02
        const batchInput = [[node.wgs84Lon, node.wgs84Lat]];
        
        const convertRes = await api.postData(
          'task-server/coordinate/batchWgs84ToGcj02',
          batchInput,
          this
        );

        const data = (convertRes && convertRes.success && Array.isArray(convertRes.data))
          ? convertRes.data
          : ((convertRes && convertRes.state != 0 && Array.isArray(convertRes.data))
            ? convertRes.data
            : null);

        // 检查data是否为[null]或包含null元素
        if (!data || data.length === 0 || data[0] === null) {
          throw new Error(this.langMap.skeletonGraphCoordConvertError[this.lang]);
        }

        // 更新节点坐标
        const nodeInList = this.editingGraphData.nodes.find(n => n.id === node.id);
        if (nodeInList) {
          nodeInList.wgs84Lon = node.wgs84Lon;
          nodeInList.wgs84Lat = node.wgs84Lat;
          nodeInList.longitude = parseFloat(data[0][0]);
          nodeInList.latitude = parseFloat(data[0][1]);
          nodeInList.lon = nodeInList.longitude;
          nodeInList.lat = nodeInList.latitude;
        }
        
        // 重新渲染地图（更新节点不缩放）
        this.$nextTick(() => {
          this.renderGraphs();
        });
        
        this.$Message.success(this.langMap.skeletonGraphNodeCoordUpdated[this.lang]);
      } catch (error) {
        this.$Notice.error({
          title: this.langMap.tip[this.lang],
          desc: error.message
        });
      }
    },

    // 处理地图点击添加节点
    async handleMapClicked(latlng) {
      if (!this.editingGraphData) return;
      
      try {
        // 转换坐标：GCJ02 -> WGS84
        const batchInput = [[latlng.lng, latlng.lat]];
        
        const convertRes = await api.postData(
          'task-server/coordinate/batchGcj02ToWgs84',
          batchInput,
          this
        );

        const data = (convertRes && convertRes.success && Array.isArray(convertRes.data))
          ? convertRes.data
          : ((convertRes && convertRes.state != 0 && Array.isArray(convertRes.data))
            ? convertRes.data
            : null);

        // 检查data是否为[null]或包含null元素
        if (!data || data.length === 0 || data[0] === null) {
          throw new Error(this.langMap.skeletonGraphCoordConvertError[this.lang]);
        }

        const wgs84Lon = parseFloat(data[0][0]);
        const wgs84Lat = parseFloat(data[0][1]);
        
        // 生成新节点ID
        const maxId = this.editingGraphData.nodes.length > 0
          ? Math.max(...this.editingGraphData.nodes.map(n => parseInt(n.id) || 0))
          : 0;
        const newNodeId = maxId + 1;
        
        // 创建新节点
        const newNode = {
          id: newNodeId,
          longitude: latlng.lng,
          latitude: latlng.lat,
          lon: latlng.lng,
          lat: latlng.lat,
          wgs84Lon: wgs84Lon,
          wgs84Lat: wgs84Lat
        };
        
        // 添加到节点列表
        this.editingGraphData.nodes.push(newNode);
        
        // 地图添加节点后，始终根据连接模式自动连线（只要开启了自动连线）
        const config = this.editingGraphData.config || {};
        if (config.autoConnect) {
          this.autoConnectNode(newNode);
        }
        
        // 重新渲染地图（地图添加节点不缩放）
        this.$nextTick(() => {
          this.renderGraphs();
        });
      } catch (error) {
        this.$Notice.error({
          title: this.langMap.tip[this.lang],
          desc: error.message
        });
      }
    },

    // 处理节点点击进行自动连线
    handleNodeClickForAutoConnect(clickedNode) {
      if (!this.selectedNodeForConnect) {
        // 第一次点击：选中起始节点
        this.selectedNodeForConnect = clickedNode;
        this.$Message.info(this.langMap.skeletonGraphNodeSelected[this.lang].replace('{id}', clickedNode.id));
      } else {
        // 第二次点击：创建连线
        const fromNode = this.selectedNodeForConnect;
        const toNode = clickedNode;
        
        if (fromNode.id === toNode.id) {
          this.$Message.warning(this.langMap.skeletonGraphCannotConnectSelf[this.lang]);
          this.selectedNodeForConnect = null;
          return;
        }
        
        // 找出两个节点之间的所有节点（按ID排序）
        const fromId = Math.min(fromNode.id, toNode.id);
        const toId = Math.max(fromNode.id, toNode.id);
        
        // 获取范围内的所有节点并按ID排序
        const nodesInRange = this.editingGraphData.nodes
          .filter(n => n.id >= fromId && n.id <= toId)
          .sort((a, b) => a.id - b.id);
        
        if (nodesInRange.length < 2) {
          this.$Message.warning(this.langMap.skeletonGraphNotEnoughNodes[this.lang]);
          this.selectedNodeForConnect = null;
          return;
        }
        
        // 创建顺序连线
        const newEdges = [];
        for (let i = 0; i < nodesInRange.length - 1; i++) {
          const from = nodesInRange[i];
          const to = nodesInRange[i + 1];
          
          // 检查连线是否已存在
          const edgeExists = this.editingGraphData.edges.some(edge => {
            return (edge.fromNodeId === from.id && edge.toNodeId === to.id) ||
                   (edge.fromNodeId === to.id && edge.toNodeId === from.id);
          });
          
          if (!edgeExists) {
            const distance = autoConnector.calculateDistance(
              from.latitude || from.lat,
              from.longitude || from.lon,
              to.latitude || to.lat,
              to.longitude || to.lon
            );
            
            newEdges.push({
              fromNodeId: from.id,
              toNodeId: to.id,
              bidirectional: 1,
              weight: distance
            });
          }
        }
        
        if (newEdges.length > 0) {
          this.editingGraphData.edges.push(...newEdges);
          this.$Message.success(this.langMap.skeletonGraphEdgesCreated[this.lang].replace('{count}', newEdges.length));
          
          // 重新渲染地图（自动连线不缩放）
          this.$nextTick(() => {
            this.renderGraphs();
          });
        } else {
          this.$Message.info(this.langMap.skeletonGraphAllEdgesExist[this.lang]);
        }
        
        // 重置选中状态
        this.selectedNodeForConnect = null;
      }
    },

    // 自动连线
    autoConnectNode(newNode) {
      const config = this.editingGraphData.config || {};
      const mode = config.connectMode || 'insert';
      const existingNodes = this.editingGraphData.nodes.filter(n => n.id !== newNode.id);
      
      if (existingNodes.length === 0) {
        // 第一个节点，记录为最后添加的节点
        this.lastAddedNodeId = newNode.id;
        return;
      }
      
      const autoConnector = require('@/utils/skeletonGraph/autoConnector').default;
      
      if (mode === 'sequential') {
        // 顺序连接：连接到最后一个添加的节点
        if (this.lastAddedNodeId) {
          const lastAddedNode = this.editingGraphData.nodes.find(n => n.id === this.lastAddedNodeId);
          
          if (lastAddedNode) {
            const distance = autoConnector.calculateDistance(
              lastAddedNode.latitude || lastAddedNode.lat,
              lastAddedNode.longitude || lastAddedNode.lon,
              newNode.latitude || newNode.lat,
              newNode.longitude || newNode.lon
            );
            
            if (!this.editingGraphData.edges) {
              this.editingGraphData.edges = [];
            }
            
            this.editingGraphData.edges.push({
              fromNodeId: this.lastAddedNodeId,
              toNodeId: newNode.id,
              bidirectional: 1,
              weight: distance
            });
          }
        }
        
        // 更新最后添加的节点ID
        this.lastAddedNodeId = newNode.id;
      } else if (mode === 'insert') {
        // 插入模式：连接到最后一个节点
        const newEdge = autoConnector.insertConnect(existingNodes, newNode);
        if (newEdge) {
          if (!this.editingGraphData.edges) {
            this.editingGraphData.edges = [];
          }
          this.editingGraphData.edges.push(newEdge);
        }
      } else if (mode === 'nearest') {
        // 最近邻：连接到最近的节点
        const newEdge = autoConnector.nearestNeighborConnect(existingNodes, newNode);
        if (newEdge) {
          if (!this.editingGraphData.edges) {
            this.editingGraphData.edges = [];
          }
          this.editingGraphData.edges.push(newEdge);
        }
      }
    },

    // 处理从地图弹窗更新节点
    async handleNodeUpdatedFromMap(data) {
      const { graphId, node } = data;
      
      if (!this.editingGraphData || this.editingGraphData.id !== graphId) return;
      
      try {
        // 批量转换坐标：WGS84 -> GCJ02
        const batchInput = [[node.wgs84Lon, node.wgs84Lat]];
        
        const convertRes = await api.postData(
          'task-server/coordinate/batchWgs84ToGcj02',
          batchInput,
          this
        );

        const convertData = (convertRes && convertRes.success && Array.isArray(convertRes.data))
          ? convertRes.data
          : ((convertRes && convertRes.state != 0 && Array.isArray(convertRes.data))
            ? convertRes.data
            : null);

        // 检查data是否为[null]或包含null元素
        if (!convertData || convertData.length === 0 || convertData[0] === null) {
          throw new Error(this.langMap.skeletonGraphCoordConvertError[this.lang]);
        }

        // 更新节点坐标
        const nodeInList = this.editingGraphData.nodes.find(n => n.id === node.id);
        if (nodeInList) {
          nodeInList.wgs84Lon = node.wgs84Lon;
          nodeInList.wgs84Lat = node.wgs84Lat;
          nodeInList.longitude = parseFloat(convertData[0][0]);
          nodeInList.latitude = parseFloat(convertData[0][1]);
          nodeInList.lon = nodeInList.longitude;
          nodeInList.lat = nodeInList.latitude;
        }
        
        // 更新地图显示
        if (this.$refs.map) {
          this.$refs.map.updateNodePosition(node.id, {
            lat: nodeInList.latitude,
            lng: nodeInList.longitude
          });
        }
        
        this.$Message.success(this.langMap.skeletonGraphNodeCoordUpdated[this.lang]);
      } catch (error) {
        this.$Notice.error({
          title: this.langMap.tip[this.lang],
          desc: error.message
        });
      }
    },

    // 处理节点拖动
    async handleNodeDragged(data) {
      const { graphId, nodeId, lat, lng } = data;
      
      if (!this.editingGraphData || this.editingGraphData.id !== graphId) return;
      
      try {
        // 转换坐标：GCJ02 -> WGS84
        const batchInput = [[lng, lat]];
        
        const convertRes = await api.postData(
          'task-server/coordinate/batchGcj02ToWgs84',
          batchInput,
          this
        );

        const convertData = (convertRes && convertRes.success && Array.isArray(convertRes.data))
          ? convertRes.data
          : ((convertRes && convertRes.state != 0 && Array.isArray(convertRes.data))
            ? convertRes.data
            : null);

        // 检查data是否为[null]或包含null元素
        if (!convertData || convertData.length === 0 || convertData[0] === null) {
          throw new Error(this.langMap.skeletonGraphCoordConvertError[this.lang]);
        }

        // 更新节点坐标
        const node = this.editingGraphData.nodes.find(n => n.id === nodeId);
        if (node) {
          node.wgs84Lon = parseFloat(convertData[0][0]);
          node.wgs84Lat = parseFloat(convertData[0][1]);
          node.longitude = lng;
          node.latitude = lat;
          node.lon = lng;
          node.lat = lat;
        }
        
        // 更新连线
        this.updateEdgesForNode(nodeId, graphId);
      } catch (error) {
        console.error('坐标转换失败:', error);
      }
    },

    // 处理从地图删除节点
    handleDeleteNodeFromMap(data) {
      const { graphId, nodeId } = data;
      
      if (!this.editingGraphData || this.editingGraphData.id !== graphId) return;
      
      // 从节点列表中删除
      const nodeIndex = this.editingGraphData.nodes.findIndex(n => n.id === nodeId);
      if (nodeIndex !== -1) {
        this.editingGraphData.nodes.splice(nodeIndex, 1);
      }
      
      // 删除所有关联的连线
      if (this.editingGraphData.edges) {
        this.editingGraphData.edges = this.editingGraphData.edges.filter(edge => {
          const fromNodeId = edge.fromNodeId || edge.from;
          const toNodeId = edge.toNodeId || edge.to;
          return fromNodeId !== nodeId && toNodeId !== nodeId;
        });
      }
      
      // 更新地图显示
      this.$nextTick(() => {
        if (this.$refs.map) {
          this.$refs.map.renderGraphs([this.editingGraphData]);
        }
      });
      
      this.$Message.success(this.langMap.skeletonGraphNodeDeleted[this.lang]);
    },

    // 处理从地图删除连线
    handleDeleteEdgeFromMap(data) {
      const { graphId, edgeId } = data;
      
      if (!this.editingGraphData || this.editingGraphData.id !== graphId) return;
      
      const [fromNodeId, toNodeId] = edgeId.split('-');
      
      // 从连线列表中删除
      if (this.editingGraphData.edges) {
        const edgeIndex = this.editingGraphData.edges.findIndex(e => {
          const fId = e.fromNodeId || e.from;
          const tId = e.toNodeId || e.to;
          return fId == fromNodeId && tId == toNodeId;
        });
        
        if (edgeIndex !== -1) {
          this.editingGraphData.edges.splice(edgeIndex, 1);
        }
      }
      
      // 重新渲染地图（删除连线不缩放）
      this.$nextTick(() => {
        this.renderGraphs();
      });
      
      this.$Message.success(this.langMap.skeletonGraphEdgeDeleted[this.lang]);
    }
  }
};
</script>

<style scoped>
/* iView Spin fix 内置 z-index 只有 8，低于地图面板（1000），需覆盖确保遮罩置顶 */
/deep/ .ivu-spin-fix {
  z-index: 1100 !important;
}
.skeleton-graph-page {
  background: #f0f2f5;
  height: 100%;
  overflow: hidden;
}

.skeleton-map-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.skeleton-map-container .map {
  width: 100%;
  height: 100%;
}

.skeleton-graph-page >>> .ivu-table .table-row-selected td {
  background-color: #e6f7ff !important;
}

.skeleton-graph-page >>> .ivu-table .table-row-selected:hover td {
  background-color: #bae7ff !important;
}

.skeleton-graph-page >>> .ivu-table-row {
  cursor: pointer;
}

.skeleton-graph-page >>> .ivu-table-row:hover {
  background-color: #f5f5f5;
}

.skeleton-graph-page >>> .ivu-card-body {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0px;
}

/* 地图弹窗样式 */
.skeleton-graph-page >>> .skeleton-graph-popup h4,
.skeleton-graph-page >>> .node-edit-popup h4,
.skeleton-graph-page >>> .edge-popup h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.skeleton-graph-page >>> .skeleton-graph-popup p,
.skeleton-graph-page >>> .edge-popup p {
  margin: 6px 0;
  font-size: 14px;
  color: #666;
}

.skeleton-graph-page >>> .edge-popup p strong {
  color: #333;
  font-weight: 600;
}

.skeleton-graph-page >>> .node-edit-popup .coord-row {
  display: flex;
  align-items: center;
  margin: 8px 0;
  font-size: 14px;
}

.skeleton-graph-page >>> .node-edit-popup .coord-row label {
  min-width: 95px;
  color: #666;
  font-weight: 500;
}

.skeleton-graph-page >>> .node-edit-popup .coord-row .coord-value {
  flex: 1;
  color: #333;
  padding: 4px 8px;
  background: #f8f8f9;
  border-radius: 4px;
  margin-right: 8px;
}

.skeleton-graph-page >>> .node-edit-popup .coord-row .edit-icon {
  color: #2d8cf0;
  cursor: pointer;
  font-size: 16px;
  transition: color 0.3s;
}

.skeleton-graph-page >>> .node-edit-popup .coord-row .edit-icon:hover {
  color: #5cadff;
}

.skeleton-graph-page >>> .node-edit-popup .coord-row .coord-input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  margin-right: 8px;
}

.skeleton-graph-page >>> .node-edit-popup .coord-row .coord-input:focus {
  border-color: #2d8cf0;
  box-shadow: 0 0 0 2px rgba(45, 140, 240, 0.2);
}

.skeleton-graph-page >>> .node-edit-popup .coord-row .confirm-icon {
  color: #19be6b;
  cursor: pointer;
  font-size: 18px;
  transition: color 0.3s;
}

.skeleton-graph-page >>> .node-edit-popup .coord-row .confirm-icon:hover {
  color: #47cb89;
}

.skeleton-graph-page >>> .coord-input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #2d8cf0;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  box-shadow: 0 0 0 2px rgba(45, 140, 240, 0.2);
}

.skeleton-graph-page >>> .delete-btn {
  margin-top: 12px;
  padding: 6px 16px;
  font-size: 13px;
  color: #fff;
  background-color: #ed4014;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;
}

.skeleton-graph-page >>> .delete-btn:hover {
  background-color: #f16643;
}

.skeleton-graph-page >>> .leaflet-popup-content-wrapper {
  border-radius: 8px;
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.2);
}

.skeleton-graph-page >>> .leaflet-popup-content {
  margin: 16px;
  min-width: 200px;
}

.demo-spin-icon-load {
  animation: demo-spin 1s linear infinite;
}

@keyframes demo-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 数据导入样式 */
.import-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8eaec;
}

.import-result {
  margin-top: 12px;
}

.result-summary {
  margin-top: 10px;
  padding: 10px;
  background: #f8f8f9;
  border-radius: 4px;
  font-size: 14px;
}

.import-errors {
  margin-top: 12px;
}

.error-list {
  margin: 10px 0 0 20px;
  color: #ed4014;
  max-height: 150px;
  overflow-y: auto;
}

.error-list li {
  margin: 5px 0;
  font-size: 13px;
}

/* 编辑模式工具栏 */
.edit-toolbar {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background: white;
  padding: 10px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
}

.edit-mode-label {
  font-weight: 500;
  color: #1890ff;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 箭头装饰器样式 */
.skeleton-graph-page >>> .arrow-decorator {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.skeleton-graph-page >>> .arrow-decorator svg {
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
}
</style>
